import * as Sentry from '@sentry/react';
import { getCurrentSessionURL } from '../../utils/fullstory/fullstory';

const SENTRY_DSN =
  'https://b71ba64d5d7dd1b33c33f09e74656419@o99218.ingest.us.sentry.io/4511473395105792';

const SENSITIVE_FIELDS = [
  'email',
  'fullName',
  'userName',
  'accountName',
  'churchName',
  'password',
  'token',
  'authorization',
  'cookie',
  'sessionId',
];

const SCRUBBED = '[Filtered]';

/**
 * Recursively scrub sensitive fields from a plain object.
 * Returns a new object — the original is not mutated.
 */
const scrubObject = (obj) => {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(scrubObject);

  return Object.keys(obj).reduce((acc, key) => {
    const lowerKey = key.toLowerCase();
    if (
      SENSITIVE_FIELDS.some((field) => lowerKey.includes(field.toLowerCase()))
    ) {
      acc[key] = SCRUBBED;
    } else {
      acc[key] = scrubObject(obj[key]);
    }
    return acc;
  }, {});
};

/**
 * Strip email-like patterns from strings to prevent PII leakage
 * in error messages or exception values.
 */
const scrubEmailsFromString = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    '[email]'
  );
};

/**
 * Attach the current FullStory session-replay URL to the Sentry event
 * context. This links each Sentry issue to the FullStory session in which
 * it occurred, giving a tighter debugging / feedback loop.
 *
 * The URL contains no PII (it's an opaque session identifier), so it is
 * safe to send.
 */
const addFullStoryContext = (event) => {
  const fullStoryUrl = getCurrentSessionURL(true);
  if (!fullStoryUrl) return event;

  event.contexts = event.contexts || {};
  event.contexts.fullStory = {
    ...event.contexts.fullStory,
    fullStoryUrl,
  };

  // Surface the link as a searchable/filterable tag in Sentry as well.
  event.tags = event.tags || {};
  event.tags.fullStory = true;

  // Reverse link: record the Sentry event inside the FullStory session so
  // the error is visible/searchable while watching a replay.
  try {
    if (window.FS && typeof window.FS.event === 'function' && event.event_id) {
      const [primaryException] = event.exception?.values || [];
      window.FS.event('Sentry Error', {
        sentryEventId: event.event_id,
        sentryErrorType: primaryException?.type,
        sentryErrorMessage: scrubEmailsFromString(primaryException?.value),
      });
    }
  } catch (error) {
    // Never let the reverse link break error reporting.
  }

  return event;
};

/**
 * Scrub PII from a Sentry event before it is sent.
 */
const beforeSend = (event) => {
  // Never send user PII
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  // Scrub request headers (e.g. Authorization)
  if (event.request && event.request.headers) {
    event.request.headers = scrubObject(event.request.headers);
  }

  // Scrub request data/body
  if (event.request && event.request.data) {
    if (typeof event.request.data === 'object') {
      event.request.data = scrubObject(event.request.data);
    } else if (typeof event.request.data === 'string') {
      event.request.data = scrubEmailsFromString(event.request.data);
    }
  }

  // Scrub exception values for email patterns
  if (event.exception && event.exception.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: scrubEmailsFromString(ex.value),
    }));
  }

  // Scrub extra context
  if (event.extra) {
    event.extra = scrubObject(event.extra);
  }

  // Scrub contexts
  if (event.contexts) {
    event.contexts = scrubObject(event.contexts);
  }

  // Link the event to its FullStory session (added after scrubbing so the
  // opaque, PII-free session URL is preserved).
  return addFullStoryContext(event);
};

/**
 * Scrub PII from breadcrumbs (e.g. XHR breadcrumbs that may
 * contain auth tokens or user data in URLs/headers).
 */
const beforeBreadcrumb = (breadcrumb) => {
  // Drop console.warn breadcrumbs and any message starting with "Warning:"
  if (
    (breadcrumb.category === 'console' && breadcrumb.level === 'warning') ||
    /^warning:/i.test(breadcrumb.message)
  ) {
    return null;
  }

  if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
    if (breadcrumb.data) {
      breadcrumb.data = scrubObject(breadcrumb.data);
    }
  }

  if (breadcrumb.message) {
    breadcrumb.message = scrubEmailsFromString(breadcrumb.message);
  }

  return breadcrumb;
};

function deriveEnvironment() {
  const { hostname } = window.location;
  if (hostname.includes('local')) {
    return 'mlc-local';
  } else if (hostname.includes('dev') || hostname.includes('int')) {
    return 'mlc-int';
  } else if (hostname.includes('stage') || hostname.includes('uat')) {
    return 'mlc-uat';
  } else {
    return 'mlc-prod';
  }
}

const initSentry = () => {
  if (!SENTRY_DSN) {
    return;
  }

  const isProduction = deriveEnvironment() === 'mlc-prod';

  const alwaysIgnoreErrors = [
    // OIDC Client Library Errors
    'No matching state found in storage',
    'Worker was terminated',
    'Load failed',
    // Authorization errors are not sent because they are used to determine if the user is logged in
    'Request failed with status code 403',
    // Fullstory API error
    'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
    // Test error
    'Error check',
  ];

  const ignoreErrors = isProduction
    ? alwaysIgnoreErrors
    : [...alwaysIgnoreErrors, 'Warning: ', 'warning: '];

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: deriveEnvironment(),

    // Capture 100% of error events — no sampling for errors
    sampleRate: 1.0,

    // Do not send PII by default (cookies, user IP, etc.)
    sendDefaultPii: false,

    beforeSend,
    beforeBreadcrumb,

    // Limit breadcrumbs to keep payload size reasonable
    maxBreadcrumbs: 50,

    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],

    // Ignore common non-actionable errors to reduce noise in Sentry
    ignoreErrors: ignoreErrors,
  });
};

export {
  initSentry,
  beforeSend,
  beforeBreadcrumb,
  scrubObject,
  scrubEmailsFromString,
  addFullStoryContext,
};
export default initSentry;
